import { For, Show, createSignal } from "solid-js";
import { DateTimePicker } from "./DateTimePicker";
import { MarkdownEditor } from "./MarkdownEditor";
import { RecurrenceFields, type RecurrenceFreq } from "./RecurrenceFields";
import { CommentsSection } from "./CommentsSection";
import { DependenciesSection } from "./DependenciesSection";
import { AddToProject } from "./AddToProject";
import { taskClient } from "~/data/clients";
import { displayName, initial, memberSwatch } from "~/lib/derive";
import type { Member, Task } from "@longhouse/client";

/**
 * Inline detail-and-edit panel for a single task. Lives directly under
 * the task row in any list view (Tasks page, project detail). Surfaces
 * every editable field — title, description, tag, due, estimate, status,
 * assignees, recurrence — plus a delete action and an "Add to project"
 * control (see AddToProject.tsx).
 *
 * Behavior: every field is an editable form control from the start; there
 * is no "view then edit" two-step. Save pushes a single UpdateTask call;
 * close just collapses without saving. The parent owns the open/closed
 * state via its own signal and re-fetches the task list on save so the
 * row above reflects the new values.
 */

interface Props {
  task: Task;
  members: Member[];
  onClose: () => void;
  onSaved: () => Promise<unknown>;
  /** Optional: confirm-and-delete this task. When omitted, no delete button. */
  onDelete?: () => Promise<unknown>;
}

const TASK_STATUSES = ["open", "in_progress", "done", "cancelled"] as const;

export const TaskDetailEditor = (props: Props) => {
  // We seed each control from the task and own the edit state locally;
  // saving sends the full updated Task back through UpdateTask.
  const [title, setTitle] = createSignal(props.task.title);
  const [description, setDesc] = createSignal(props.task.description ?? "");
  const [tag, setTag] = createSignal(props.task.tag ?? "");
  const [due, setDue] = createSignal(props.task.dueAt ?? "");
  const [estimate, setEstimate] = createSignal(
    props.task.estimateMinutes !== undefined ? String(props.task.estimateMinutes) : "",
  );
  const [status, setStatus] = createSignal<string>(
    typeof props.task.status === "string" ? props.task.status : "open",
  );
  const [assignees, setAssignees] = createSignal<string[]>((props.task.assignees ?? []).slice());
  const initRecFreq = (): RecurrenceFreq =>
    (typeof props.task.recurrenceFreq === "string" ? props.task.recurrenceFreq : "") as RecurrenceFreq;
  const [recFreq, setRecFreq] = createSignal<RecurrenceFreq>(initRecFreq());
  const [recInterval, setRecInterval] = createSignal(props.task.recurrenceInterval ?? 1);
  const [recNextAt, setRecNextAt] = createSignal(props.task.nextRecurrenceAt ?? "");
  const [recByWeekday, setRecByWeekday] = createSignal<number[]>(
    Array.isArray(props.task.recurrenceByWeekday)
      ? props.task.recurrenceByWeekday.map((n) => Number(n))
      : [],
  );
  const [recBySetpos, setRecBySetpos] = createSignal(props.task.recurrenceBySetpos ?? 1);
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal<string | null>(null);

  const toggleAssignee = (id: string) =>
    setAssignees((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async (e: SubmitEvent) => {
    e.preventDefault();
    if (busy()) return;
    setBusy(true);
    setErr(null);
    try {
      const body: any = {
        ...props.task,
        title: title().trim() || props.task.title,
        description: description(),
        tag: tag().trim() || undefined,
        dueAt: due() || undefined,
        estimateMinutes: estimate() ? Number(estimate()) : undefined,
        status: status(),
        assignees: assignees(),
        recurrenceFreq: recFreq() || "",
        recurrenceInterval: recFreq() ? recInterval() : undefined,
        nextRecurrenceAt: recNextAt() || undefined,
        recurrenceByWeekday: recFreq() ? recByWeekday() : [],
        recurrenceBySetpos:
          recFreq() === "monthly" || recFreq() === "quarterly" || recFreq() === "yearly"
            ? recBySetpos()
            : 0,
      };
      await taskClient.updateTask(body);
      await props.onSaved();
      props.onClose();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!props.onDelete) return;
    if (!confirm(`Delete "${props.task.title}"?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await props.onDelete();
      props.onClose();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style="margin:6px 0 12px 38px;padding:14px 16px;background:var(--paper);border:1px solid var(--line);border-radius:var(--r-md);box-shadow:var(--shadow-low);display:flex;flex-direction:column;gap:10px">
    <form onSubmit={save} style="display:flex;flex-direction:column;gap:10px">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">
        <label style="display:flex;flex-direction:column;gap:4px;grid-column:1/-1">
          <span style="font-size:11px;color:var(--ink-mute)">Title</span>
          <input
            type="text" value={title()} onInput={(e) => setTitle(e.currentTarget.value)}
            required
            style="padding:7px 10px;border:1px solid var(--line);border-radius:var(--r-md);background:var(--paper);font-size:14px"
          />
        </label>
        <label style="display:flex;flex-direction:column;gap:4px;grid-column:1/-1">
          <span style="font-size:11px;color:var(--ink-mute)">Description (markdown — click to edit)</span>
          <MarkdownEditor
            value={description()}
            onChange={setDesc}
            placeholder="Click to add a description (markdown supported)…"
            rows={3}
          />
        </label>
        <label style="display:flex;flex-direction:column;gap:4px">
          <span style="font-size:11px;color:var(--ink-mute)">Status</span>
          <select
            value={status()} onChange={(e) => setStatus(e.currentTarget.value)}
            style="padding:6px 10px;border:1px solid var(--line);border-radius:var(--r-md);background:var(--paper);font-size:13px"
          >
            <For each={TASK_STATUSES}>{(s) => <option value={s}>{s.replace("_", " ")}</option>}</For>
          </select>
        </label>
        <label style="display:flex;flex-direction:column;gap:4px">
          <span style="font-size:11px;color:var(--ink-mute)">Tag</span>
          <input
            type="text" value={tag()} onInput={(e) => setTag(e.currentTarget.value)}
            placeholder="house, barn, …"
            style="padding:7px 10px;border:1px solid var(--line);border-radius:var(--r-md);background:var(--paper);font-size:13px"
          />
        </label>
        <label style="display:flex;flex-direction:column;gap:4px">
          <span style="font-size:11px;color:var(--ink-mute)">Due</span>
          <DateTimePicker value={due()} onChange={setDue} title="Due (optional)" />
        </label>
        <label style="display:flex;flex-direction:column;gap:4px">
          <span style="font-size:11px;color:var(--ink-mute)">Estimate (min)</span>
          <input
            type="number" min="0" value={estimate()} onInput={(e) => setEstimate(e.currentTarget.value)}
            style="padding:7px 10px;border:1px solid var(--line);border-radius:var(--r-md);background:var(--paper);font-size:13px"
          />
        </label>
      </div>

      <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center">
        <span style="font-size:11px;color:var(--ink-mute);margin-right:4px">Assignees</span>
        <For each={props.members}>
          {(m) => {
            const on = () => assignees().includes(m.memberId);
            return (
              <button
                type="button"
                onClick={() => toggleAssignee(m.memberId)}
                style={`display:inline-flex;align-items:center;gap:4px;padding:3px 8px 3px 3px;border-radius:9999px;font-size:12px;cursor:pointer;border:1px solid ${on() ? "var(--grass-4)" : "var(--line)"};background:${on() ? "color-mix(in oklab, var(--grass-1) 30%, var(--paper))" : "var(--paper)"};color:var(--ink)`}
              >
                <span class={`a sm ${memberSwatch(m.memberId)}`} style="width:18px;height:18px;font-size:10px">{initial(m)}</span>
                {displayName(m)}
              </button>
            );
          }}
        </For>
      </div>

      <RecurrenceFields
        freq={recFreq}
        setFreq={setRecFreq}
        interval={recInterval}
        setInterval={setRecInterval}
        nextAt={recNextAt}
        setNextAt={setRecNextAt}
        byWeekday={recByWeekday}
        setByWeekday={setRecByWeekday}
        bySetpos={recBySetpos}
        setBySetpos={setRecBySetpos}
      />

      <Show when={err()}>{(m) => <span style="color:var(--rust);font-size:12px">{m()}</span>}</Show>

      <div style="display:flex;gap:8px;justify-content:flex-end">
        <Show when={props.onDelete}>
          <button type="button" class="btn btn-ghost" onClick={remove} disabled={busy()} style="color:var(--rust);margin-right:auto;padding:6px 12px">
            Delete
          </button>
        </Show>
        <button type="button" class="btn btn-ghost" onClick={props.onClose} disabled={busy()} style="padding:6px 12px">Close</button>
        <button type="submit" class="btn btn-primary" disabled={busy() || !title().trim()} style="padding:6px 14px">
          {busy() ? "Saving…" : "Save"}
        </button>
      </div>
    </form>

      {/* Attaching is its own call, not part of Save. onSaved doubles as
          the parent's refetch hook so the row moves (e.g. out of the
          dashboard's no-project list) right away. */}
      <AddToProject task={props.task} onAdded={props.onSaved} />

      <DependenciesSection nodeType="task" nodeId={props.task.taskId} houseId={props.task.houseId} />

      <CommentsSection
        targetType="task"
        targetId={props.task.taskId}
        houseId={props.task.houseId}
        members={props.members}
      />
    </div>
  );
};
